import { useState, useEffect } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, DollarSign, Sparkles, Store } from 'lucide-react';

export default function VendorPricingApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor-pricing/pending-proposals');
      setItems(r.data.items || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const decide = async (item, action) => {
    setBusyId(item.id);
    try {
      await api.post(`/vendor-pricing/pricing/items/${item.id}/${action}`, { note: '' });
      toast.success(action === 'approve' ? 'Approved & live' : 'Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5 animate-fade-in max-w-5xl" data-testid="vendor-pricing-approvals">
      <div className="pb-4 border-b border-[#2D3F5E]/60">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// OPS REVIEW</p>
        <h1 className="font-['Manrope'] font-extrabold text-2xl flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <DollarSign size={22} className="text-[#3B82F6]" /> Vendor Pricing Proposals
        </h1>
        <p className="font-['IBM_Plex_Sans'] text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          {items.length} proposal{items.length === 1 ? '' : 's'} waiting · approve to push live to the Best Vendor Finder
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={20} style={{ color: 'var(--primary)' }} /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center card-slate">
          <CheckCircle2 size={32} className="mx-auto mb-3" style={{ color: 'var(--success)' }} />
          <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>All caught up — no pending proposals.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const isEdit = !!item.proposed_change;
            const proposed = item.proposed_change || {};
            const kind = isEdit
              ? (proposed._delete ? 'DELETE' : 'EDIT')
              : 'NEW';
            const kindColors = {
              NEW: { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.4)', text: '#60A5FA' },
              EDIT: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.4)', text: '#FCD34D' },
              DELETE: { bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.4)', text: '#F87171' },
            }[kind];
            return (
              <div key={item.id} className="card-slate p-4" data-testid={`proposal-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border font-['JetBrains_Mono']"
                        style={{ background: kindColors.bg, borderColor: kindColors.border, color: kindColors.text }}>
                        {kind}
                      </span>
                      <span className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                        <Store size={12} className="inline mr-1" />{item.vendor_name}
                      </span>
                      {item.source === 'VENDOR_PROPOSAL' && (
                        <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>vendor-submitted</span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      <div>
                        <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Category</p>
                        <p className="text-xs font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>
                          {proposed.category || item.category}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Code</p>
                        <p className="text-xs font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>
                          {proposed.service_code || item.service_code || '—'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Description</p>
                        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {proposed.description || item.description}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mt-2">
                      <div>
                        <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">
                          {isEdit && !proposed._delete ? 'New Price' : 'Price'}
                        </p>
                        <p className="text-sm font-bold font-['JetBrains_Mono']" style={{ color: 'var(--success)' }}>
                          ${(proposed.unit_price !== undefined ? proposed.unit_price : item.unit_price)?.toFixed(2)}
                          {' /'}{proposed.unit || item.unit}
                        </p>
                        {isEdit && !proposed._delete && proposed.unit_price !== undefined && proposed.unit_price !== item.unit_price && (
                          <p className="text-[10px] text-[#5C6A7A] line-through font-['JetBrains_Mono']">was ${item.unit_price?.toFixed(2)}</p>
                        )}
                      </div>
                      <div className="col-span-3">
                        <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Notes</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{proposed.notes || item.notes || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button onClick={() => decide(item, 'approve')} disabled={busyId === item.id}
                      data-testid={`approve-${item.id}`}
                      className="px-3 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wide bg-green-900/40 text-green-300 border border-green-700/50 hover:bg-green-900/60 inline-flex items-center gap-1 disabled:opacity-40">
                      <CheckCircle2 size={11} /> Approve
                    </button>
                    <button onClick={() => decide(item, 'reject')} disabled={busyId === item.id}
                      data-testid={`reject-${item.id}`}
                      className="px-3 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-wide bg-red-900/30 text-red-300 border border-red-700/50 hover:bg-red-900/50 inline-flex items-center gap-1 disabled:opacity-40">
                      <XCircle size={11} /> Reject
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
